import { exactCurrencyFormatter } from "@/utils"

function normalize_message_text(value: string | null | undefined): string {
  if (!value) return ""
  return value
    .replace(/<[^>]*>/g, " ")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
}

function normalize_amount_cents(amount: number): number {
  return Math.round(Math.abs(amount) * 100)
}

function amount_tokens(amount: number): string[] {
  const abs = Math.abs(amount)
  const formatted = exactCurrencyFormatter(abs)
  const tokens = new Set<string>()
  tokens.add(normalize_message_text(formatted))
  tokens.add(normalize_message_text(formatted.replace(/\$/g, "").replace(/,/g, "")))
  tokens.add(abs.toFixed(2))
  tokens.add(abs.toString())
  return [...tokens].filter(Boolean)
}

function extract_dollar_amounts(text: string): number[] {
  const amounts: number[] = []
  const regex = /\$?\s*([\d,]+\.\d{2})\b/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    const parsed = Number.parseFloat(match[1].replace(/,/g, ""))
    if (!Number.isNaN(parsed)) {
      amounts.push(parsed)
    }
  }
  return amounts
}

function amounts_match_target(extracted: number, target_cents: number): boolean {
  return Math.abs(normalize_amount_cents(extracted) - target_cents) <= 1
}

export function sources_requiring_amount_suffix(source: string): boolean {
  return source === "kehe" || source === "wakefern"
}

export function build_dispute_amount_suffix(amount: number): string {
  return `Please refund the disputed amount of ${exactCurrencyFormatter(amount)}.`
}

export function message_mentions_dispute_amount(message: string, amount: number): boolean {
  if (!message.trim() || !amount) return false

  const normalized = normalize_message_text(message)
  const target_cents = normalize_amount_cents(amount)

  for (const token of amount_tokens(amount)) {
    if (token && normalized.includes(token)) {
      return true
    }
  }

  const extracted_amounts = extract_dollar_amounts(message)
  for (const extracted of extracted_amounts) {
    if (amounts_match_target(extracted, target_cents)) {
      return true
    }
  }

  const has_repay_phrase =
    normalized.includes("repay") || normalized.includes("refund the disputed amount")
  if (has_repay_phrase) {
    for (const extracted of extracted_amounts) {
      if (amounts_match_target(extracted, target_cents)) {
        return true
      }
    }
  }

  return false
}

export function strip_dispute_amount_suffix(message: string, amount: number): string {
  if (!amount) return message

  const suffix = build_dispute_amount_suffix(amount)
  const trimmed = message.trimEnd()
  const patterns = [`.\n\n${suffix}`, `\n\n${suffix}`, `\n${suffix}`, ` ${suffix}`, suffix]

  for (const pattern of patterns) {
    if (trimmed.endsWith(pattern)) {
      return trimmed.slice(0, -pattern.length).trimEnd()
    }
  }

  return message
}

export function compose_dispute_message(message: string, amount: number, source: string): string {
  if (!sources_requiring_amount_suffix(source) || !amount) {
    return message
  }
  if (message_mentions_dispute_amount(message, amount)) {
    return message
  }

  const suffix = build_dispute_amount_suffix(amount)
  const trimmed = message.trimEnd()
  if (!trimmed) {
    return suffix
  }

  const separator = trimmed.endsWith(".") ? "\n\n" : ".\n\n"
  return `${trimmed}${separator}${suffix}`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("sources_requiring_amount_suffix", () => {
    it("returns true for kehe and wakefern", () => {
      expect(sources_requiring_amount_suffix("kehe")).toBe(true)
      expect(sources_requiring_amount_suffix("wakefern")).toBe(true)
    })

    it("returns false for other sources", () => {
      expect(sources_requiring_amount_suffix("unfi")).toBe(false)
    })
  })

  describe("message_mentions_dispute_amount", () => {
    it("detects formatted currency", () => {
      expect(message_mentions_dispute_amount("Please repay $97.52", 97.52)).toBe(true)
    })

    it("detects plain decimal amount", () => {
      expect(message_mentions_dispute_amount("deducted amount of 97.52", 97.52)).toBe(true)
    })

    it("detects repay phrasing with amount", () => {
      expect(
        message_mentions_dispute_amount(
          "Please repay the deducted amount of $1,027.04. Thank you.",
          1027.04,
        ),
      ).toBe(true)
    })

    it("returns false when amount is absent", () => {
      expect(message_mentions_dispute_amount("Please repay this deduction.", 97.52)).toBe(false)
    })
  })

  describe("compose_dispute_message", () => {
    it("appends suffix for kehe when amount is missing", () => {
      const result = compose_dispute_message("Waybill attached.", 97.52, "kehe")
      expect(result).toContain("Please refund the disputed amount of $97.52.")
      expect(result.startsWith("Waybill attached.")).toBe(true)
    })

    it("appends suffix for wakefern when amount is missing", () => {
      const result = compose_dispute_message("Invalid charge.", 50, "wakefern")
      expect(result).toContain("Please refund the disputed amount of $50.00.")
    })

    it("does not append when amount is already mentioned", () => {
      const message = "Please repay the deducted amount of $97.52."
      expect(compose_dispute_message(message, 97.52, "kehe")).toBe(message)
    })

    it("does not append for unfi", () => {
      expect(compose_dispute_message("Hello", 97.52, "unfi")).toBe("Hello")
    })

    it("does not double-append when suffix is already present", () => {
      const message = compose_dispute_message("Hello", 97.52, "kehe")
      expect(compose_dispute_message(message, 97.52, "kehe")).toBe(message)
    })
  })

  describe("strip_dispute_amount_suffix", () => {
    it("removes trailing auto suffix", () => {
      const composed = compose_dispute_message("Hello", 97.52, "kehe")
      expect(strip_dispute_amount_suffix(composed, 97.52)).toBe("Hello")
    })

    it("re-composes with a new amount after strip", () => {
      const composed = compose_dispute_message("Hello", 97.52, "kehe")
      const stripped = strip_dispute_amount_suffix(composed, 97.52)
      const updated = compose_dispute_message(stripped, 120, "kehe")
      expect(updated).toContain("$120.00")
      expect(updated).not.toContain("$97.52")
    })
  })
}
